import { useEffect, useMemo, useState } from 'react';
import { imageDataToSlatRects } from './imageDataToSlatRects';
import {
  drawImageContainedToGrid,
  gridSizeForImage,
  loadImageElement,
} from './loadImageToMask';

const defaultViewBox = { width: 200, height: 200 };

/**
 * @param {string | null | undefined} src
 * @param {object} [options]
 * @param {number} [options.rowCount=64]
 * @param {{ width: number, height: number }} [options.viewBox]
 * @param {number} [options.barFillRatio]
 * @param {number} [options.padding]
 * @param {number} [options.threshold]
 * @param {boolean} [options.invert]
 * @param {number} [options.minRunWidthPx]
 * @param {boolean} [options.smoothing]
 * @param {{ gridWidth: number, gridHeight: number } | null} [options.fixedGrid] - override grid (e.g. morph)
 */
export function useSlatModelFromImage(src, options = {}) {
  const rowCount = options.rowCount ?? 64;
  const viewBox = options.viewBox ?? defaultViewBox;
  const classifyOptions = useMemo(
    () => ({
      threshold: options.threshold,
      invert: options.invert,
      alphaCutoff: options.alphaCutoff,
    }),
    [options.threshold, options.invert, options.alphaCutoff],
  );
  const layoutOptions = useMemo(
    () => ({
      barFillRatio: options.barFillRatio,
      padding: options.padding,
      opacity: options.opacity,
      minRunWidthPx: options.minRunWidthPx,
    }),
    [options.barFillRatio, options.padding, options.opacity, options.minRunWidthPx],
  );

  const fixedGrid = options.fixedGrid ?? null;

  const [state, setState] = useState({
    status: 'idle',
    rects: [],
    error: null,
    gridWidth: 0,
    gridHeight: rowCount,
  });

  useEffect(() => {
    if (!src) {
      setState({ status: 'idle', rects: [], error: null, gridWidth: 0, gridHeight: rowCount });
      return undefined;
    }

    let cancelled = false;
    setState((s) => ({ ...s, status: 'loading', error: null }));

    loadImageElement(src)
      .then((img) => {
        if (cancelled) return;
        const grid = fixedGrid ?? gridSizeForImage(img, rowCount);
        const imageData = drawImageContainedToGrid(
          img,
          grid.gridWidth,
          grid.gridHeight,
          { smoothing: options.smoothing ?? false },
        );
        const { rects } = imageDataToSlatRects(imageData, viewBox, classifyOptions, layoutOptions);
        if (cancelled) return;
        setState({
          status: 'ready',
          rects,
          error: null,
          gridWidth: grid.gridWidth,
          gridHeight: grid.gridHeight,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          status: 'error',
          rects: [],
          error: err instanceof Error ? err : new Error(String(err)),
          gridWidth: 0,
          gridHeight: rowCount,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [
    src,
    rowCount,
    viewBox.width,
    viewBox.height,
    fixedGrid?.gridWidth,
    fixedGrid?.gridHeight,
    classifyOptions,
    layoutOptions,
    options.smoothing,
  ]);

  return state;
}
